<template>
  <form
    class="pre-chat-form p-4 custom-pre-chat-form"
    @submit.prevent="onSubmit"
  >
  <div class="custom-form">
    <div v-if="options.preChatMessage" class="text-black-800 text-sm leading-5 pre-chat-message">
      {{ options.preChatMessage }}
    </div>
    <form-input
      v-if="options.enabledFields.fullName"
      v-model="fullName"
      @onBlur="$v.fullName.$touch"
      maxlength="50"
      class="mt-5"
      :label="$t('PRE_CHAT_FORM.FIELDS.FULL_NAME.LABEL')"
      :placeholder="$t('PRE_CHAT_FORM.FIELDS.FULL_NAME.PLACEHOLDER')"
      type="text"
      :error="
        $v.fullName.$error ? $t('PRE_CHAT_FORM.FIELDS.FULL_NAME.ERROR') : ''
      "
    />
    <form-input
      v-if="options.enabledFields.emailAddress"
      @onBlur="$v.emailAddress.$touch"
      v-model="emailAddress"
      maxlength="50"
      class="mt-5"
      :label="$t('PRE_CHAT_FORM.FIELDS.EMAIL_ADDRESS.LABEL')"
      :placeholder="$t('PRE_CHAT_FORM.FIELDS.EMAIL_ADDRESS.PLACEHOLDER')"
      type="text"
      :error="
        $v.emailAddress.$error
          ? $t('PRE_CHAT_FORM.FIELDS.EMAIL_ADDRESS.ERROR')
          : ''
      "
    />
    <FormTelInput
      v-if="options.enabledFields.phoneNumber && isCountryCodeEnabled"
      v-model="phoneNumber"
      ref="telInput"
      :label="$t('PRE_CHAT_FORM.FIELDS.PHONE_NUMBER.LABEL')"
      :dropDownImageUrl="dropDownImageUrl"
      :dropDownSymbolAlt="dropDownSymbolAlt"
      @onBlur="$v.phoneNumber.$touch"
      @country-changed="onCountryChanged"
    />
    <form-input
      v-if="options.enabledFields.phoneNumber && !isCountryCodeEnabled"
      @onBlur="$v.phoneNumber.$touch"
      v-model="phoneNumber"
      maxlength="50"
      class="mt-5"
      :label="$t('PRE_CHAT_FORM.FIELDS.PHONE_NUMBER.LABEL')"
      :placeholder="$t('PRE_CHAT_FORM.FIELDS.PHONE_NUMBER.PLACEHOLDER')"
      type="text"
      :error="
        $v.phoneNumber.$error
          ? $t('PRE_CHAT_FORM.FIELDS.PHONE_NUMBER.ERROR')
          : ''
      "
    />
    <form-text-area
      v-if="options.enabledFields.preChatMessage"
      v-model="message"
      @onBlur="$v.message.$touch"
      class="my-5"
      :label="$t('PRE_CHAT_FORM.FIELDS.MESSAGE.LABEL')"
      :placeholder="$t('PRE_CHAT_FORM.FIELDS.MESSAGE.PLACEHOLDER')"
      :error="
        $v.message.$error
          ? message.length !== 0
            ? $t('PRE_CHAT_FORM.FIELDS.MESSAGE.ERROR')
            : $t('PRE_CHAT_FORM.FIELDS.MESSAGE.PLACEHOLDER')
          : ''
      "
    />
    <hiver-branding />
      </div>
     <custom-button
        class="font-medium pre-chat-form-submit custom-chat-button send-message-mail-button"
        block
        :bg-color="widgetColor"
        :text-color="textColor"
        :disabled="isCreating || isLoading || noFieldsEnabled"
        >
        <spinner v-if="isCreating || isLoading || noFieldsEnabled" class="p-0" />
        {{ $t('START_CONVERSATION') }}
        <img :src="sendMessageArrowIcon" alt="Send Icon" />
   </custom-button>

  </form>

</template>

<script>
import CustomButton from 'shared/components/Button';
import FormInput from '../Form/Input';
import FormTextArea from '../Form/TextArea';
import FormTelInput from '../Form/TelInput'
import Spinner from 'shared/components/Spinner';
import HiverBranding from 'widget/components/HiverBranding.vue';
import { mapGetters } from 'vuex';
import { getContrastingTextColor } from '@chatwoot/utils';
import {
  required,
  minLength,
  maxLength,
  email
} from 'vuelidate/lib/validators';
import { emailFormatter } from 'widget/helpers/utils.js';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import configMixin from '../../mixins/configMixin';
import { CAMPAIGN_TRIGGER } from 'shared/constants/conversation';
import 'vue-tel-input/dist/vue-tel-input.css';
import { PRECHAT_PHONE_NUMBER_ERROR_MESSAGE } from '../../helpers/constants';
import sendMessageArrowIcon from '../../assets/images/sendMessageArrowIcon.svg';
import { loggerInstance } from '../../../utils/hiver-logs';

export default {
  components: {
    FormInput,
    FormTextArea,
    CustomButton,
    Spinner,
    HiverBranding,
    FormTelInput,
  },
  mixins: [configMixin],
  props: {
    options: {
      type: Object,
      default: () => ({})
    },
    dropDownImageUrl: {
      type: String,
      required: true
    },
    dropDownSymbolAlt: {
      type: String,
      default: 'Image',
    }
  },
  validations() {
    const fullNameValidation = {
      fullName: {
        required: this.options?.requiredFields?.fullName ? required : {},
        maxLength: maxLength(50)
      }
    };

    const emailAddressValidation = {
      emailAddress: {
        required: this.options?.requiredFields?.emailAddress ? required : {},
        email: val => email(emailFormatter(val)),
        maxLength: maxLength(50)
      }
    };

    const phoneNumberValidation = {
      phoneNumber: {
        required: this.options?.requiredFields?.phoneNumber ? required : {},
        isPhoneE164OrEmpty,
        maxLength: maxLength(50)
      }
    };

    const messageValidation = {
      message: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(1000)
      }
    };

    let identityValidations = {};

    if (this.options.enabledFields.fullName) {
      identityValidations = { ...identityValidations, ...fullNameValidation };
    }

    if (this.options.enabledFields.emailAddress) {
      identityValidations = { ...identityValidations, ...emailAddressValidation };
    }

    if (this.options.enabledFields.phoneNumber) {
      identityValidations = { ...identityValidations, ...phoneNumberValidation };
    }

    if (this.options.enabledFields.preChatMessage) {
      identityValidations = { ...identityValidations, ...messageValidation };
    }

    return {
      ...identityValidations
    };
  },
  data() {
    return {
      fullName: '',
      emailAddress: '',
      phoneNumber: '',
      message: '',
      isLoading: false,
      countryCode: null,
      sendMessageArrowIcon,
    };
  },
  computed: {
    ...mapGetters({
      widgetColor: 'appConfig/getWidgetColor',
      isCreating: 'conversation/getIsCreating',
      isCampaignExecuted: 'campaign/getIsCampaignMessageClicked'
    }),
    textColor() {
      return getContrastingTextColor(this.widgetColor);
    },
    noFieldsEnabled() {
        if (!this.hasAConnectedAgentBot) {
            return false;
        }
        const enbFlds = this.options.enabledFields;
        return !enbFlds.fullName && !enbFlds.emailAddress && !enbFlds.phoneNumber && !enbFlds.preChatMessage
    },
    isCountryCodeEnabled() {
      return !!this.isPreChatCountryCodeEnabled;
    },
  },
  methods: {
    createNewConversation(data) {
        this.$store.dispatch('conversationAttributes/setConversationload', true);
        this.$store.dispatch('conversation/createConversation', data).then(() => {
           if (this.hasAConnectedAgentBot) {
             this.$emit('handleChatBot');
           }
           else {
             loggerInstance.addLog({ name: loggerInstance.EVENTS.NEW_CONVERSATION_AGENT, type: 'event' });
           }
        }).finally(() => {
           this.$store.dispatch('conversationAttributes/setConversationload', false);
        })
    },
    areEnabledFieldsInvalid() {
      const isPhoneNumberValid = this.options.enabledFields.phoneNumber ? !this.$refs.telInput.phoneNumberError : true;
      return (
        (this.options.enabledFields.fullName && this.$v.fullName.$invalid) ||
        (this.options.enabledFields.emailAddress && this.$v.emailAddress.$invalid) ||
        (this.options.enabledFields.phoneNumber && !isPhoneNumberValid) ||
        (this.options.enabledFields.preChatMessage && this.$v.message.$invalid)
      );
    },
    onCountryChanged(newCountryCode) {
      this.countryCode = newCountryCode;
    },
    computePhoneNumberError() {
      if(this.phoneNumber === '' && this.options.enabledFields.phoneNumber) {
          const telInputComponent = this.$refs.telInput;
          telInputComponent.phoneNumberError = PRECHAT_PHONE_NUMBER_ERROR_MESSAGE.EMPTY_PHONE_NUMBER;
        } else if(this.phoneNumber && this.options.enabledFields.phoneNumber) {
          const telInputComponent = this.$refs.telInput;
          const response = telInputComponent.validatePhoneNumber(this.phoneNumber);
          telInputComponent.phoneNumberError = response.error;
        }
    },
    onSubmit() {
      if (this.isCountryCodeEnabled) {
        this.computePhoneNumberError();
        this.$v.$touch();
        if (this.areEnabledFieldsInvalid()) {
          this.isLoading = false;
          return;
        }
      } else {
        this.$v.$touch();
        if (this.$v.$invalid) {
          this.isLoading = false;
          return;
        }
      }
      this.isLoading = true;
      this.phoneNumber = this.isCountryCodeEnabled && this.options.enabledFields.phoneNumber ? this.$refs.telInput.formattedPhoneNumber : this.phoneNumber;
      const contactData = {
                email: this.emailAddress,
                name: this.fullName,
                phone_number: this.phoneNumber,
           };
      if (this.isCampaignExecuted) {
         this.$store.dispatch('contacts/update', {
            user: {...contactData},
        }).then(() => bus.$emit('on-campaign-prechatform-submit'));
         bus.$on('on-campaign-ready', () => {
            this.$store.dispatch('conversation/sendMessage', {content: this.message});
         });
        return;
       }

    if (this.hasAConnectedAgentBot && !this.noFieldsEnabled) {
        this.$emit('handleChatBot', null, CAMPAIGN_TRIGGER.WIDGET, false, contactData);
        return;
    }

    this.createNewConversation({
        fullName: this.fullName,
        emailAddress: this.emailAddress,
        phoneNumber: this.phoneNumber,
        message: this.message
      });
    }
  }
};
</script>
